import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from './components/Navigation';
import ConversationalOnboarding from './components/ConversationalOnboarding';
import UserInputForm from './components/UserInputForm';
import GoalEntry from './components/GoalEntry';
import Dashboard from './components/Dashboard';
import Summary from './components/Summary';
import RiskProfile from './components/RiskProfile';
import InvestmentPlanning from './components/InvestmentPlanning';
import Calculators from './components/Calculators';
import Login from './components/Login';
import Pricing from './components/Pricing';
import Profile from './components/Profile';
import PendingApproval from './components/PendingApproval';
import { FinancialProvider } from './context/FinancialContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useCloudSync } from './hooks/useCloudSync';
import './App.css';

// Main layout wrapper to conditionally render Navigation
const AppLayout = () => {
  const { isAuthenticated } = useAuth();
  useCloudSync();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Only show navigation if user is authenticated */}
      {isAuthenticated && <Navigation />}

      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className={`${isAuthenticated ? 'container mx-auto px-4 py-8 max-w-7xl flex-grow' : 'flex-grow'}`}
      >
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/details" replace />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/pending-approval" element={isAuthenticated ? <PendingApproval /> : <Navigate to="/login" replace />} />

          {/* Protected Routes (active subscribers only — see ProtectedRoute) */}
          <Route path="/details" element={<ProtectedRoute><UserInputForm /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><ConversationalOnboarding /></ProtectedRoute>} />
          <Route path="/risk-profile" element={<ProtectedRoute><RiskProfile /></ProtectedRoute>} />
          <Route path="/investments" element={<ProtectedRoute><InvestmentPlanning /></ProtectedRoute>} />
          <Route path="/goals" element={<ProtectedRoute><GoalEntry /></ProtectedRoute>} />
          <Route path="/calculators" element={<ProtectedRoute><Calculators /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/summary" element={<ProtectedRoute><Summary /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

          {/* Root Redirect */}
          <Route path="/" element={<Navigate to={isAuthenticated ? "/details" : "/login"} replace />} />
        </Routes>
      </motion.main>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <FinancialProvider>
        <Router>
          <AppLayout />
        </Router>
      </FinancialProvider>
    </AuthProvider>
  );
}

export default App;